import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import TimerIcon from '@mui/icons-material/Timer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import PedalBikeIcon from '@mui/icons-material/PedalBike';
import {
  useGetWorkoutSummaryQuery,
  useGetWorkoutEquipmentAnalyticsQuery,
  useGetWorkoutCompletionAnalyticsQuery,
  useGetWorkoutProgressionAnalyticsQuery,
  useListWorkoutCoursesQuery,
  useGetSimpleWorkoutsQuery,
} from '../../../store/api/baseApi';
import KPICard from '../components/KPICard';
import PeriodSelector from '../components/PeriodSelector';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
  ArcElement
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`workout-analytics-tabpanel-${index}`}
      aria-labelledby={`workout-analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const WorkoutAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [periodDays, setPeriodDays] = useState(90);
  const [progressionMetric, setProgressionMetric] = useState('pace');
  const [selectedCourse, setSelectedCourse] = useState<number | null>(null);
  const [selectedWorkoutCourse, setSelectedWorkoutCourse] = useState<number | 'all'>('all');

  // Fetch workout courses for filtering
  const { data: workoutCoursesData } = useListWorkoutCoursesQuery({ skip: 0, limit: 100 });

  // Fetch simple workouts for filtering
  const { data: simpleWorkoutsData } = useGetSimpleWorkoutsQuery({});

  // API Queries - conditional based on active tab
  const {
    data: summaryData,
    isLoading: isLoadingSummary,
    error: summaryError,
  } = useGetWorkoutSummaryQuery(
    {
      days: periodDays,
      workout_id: selectedWorkoutCourse === 'all' ? undefined : selectedWorkoutCourse
    },
    { skip: tabValue !== 0 }
  );

  const {
    data: equipmentData,
    isLoading: isLoadingEquipment,
    error: equipmentError,
  } = useGetWorkoutEquipmentAnalyticsQuery(
    {
      days: periodDays
    },
    { skip: tabValue !== 1 }
  );

  const {
    data: completionData,
    isLoading: isLoadingCompletion,
    error: completionError,
  } = useGetWorkoutCompletionAnalyticsQuery(
    {
      days: periodDays
    },
    { skip: tabValue !== 2 }
  );

  const {
    data: progressionData,
    isLoading: isLoadingProgression,
    error: progressionError,
  } = useGetWorkoutProgressionAnalyticsQuery(
    {
      metric: progressionMetric,
      workout_id: selectedWorkoutCourse === 'all' ? undefined : selectedWorkoutCourse,
      days: periodDays,
    },
    { skip: tabValue !== 3 }
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Course Performance Table Data
  const coursePerformanceData = useMemo(() => {
    if (!summaryData) return [];
    return Object.values(summaryData.by_course).sort(
      (a, b) => b.completion_rate - a.completion_rate
    );
  }, [summaryData]);

  // Sport Distribution Chart Data
  const sportDistributionData = useMemo(() => {
    if (!summaryData) return null;
    const sportStats = Object.values(summaryData.by_sport_type);
    return {
      labels: sportStats.map((s) => s.sport_type),
      datasets: [
        {
          label: 'Workouts by Sport',
          data: sportStats.map((s) => s.count),
          backgroundColor: [
            'rgba(255, 99, 132, 0.6)',
            'rgba(54, 162, 235, 0.6)',
            'rgba(255, 206, 86, 0.6)',
            'rgba(75, 192, 192, 0.6)',
            'rgba(153, 102, 255, 0.6)',
          ],
        },
      ],
    };
  }, [summaryData]);

  // Equipment Usage Data
  const equipmentCards = useMemo(() => {
    if (!equipmentData) return [];
    return equipmentData.most_used_equipment.slice(0, 6); // Top 6
  }, [equipmentData]);

  // Completion by Period Chart Data
  const completionByPeriodData = useMemo(() => {
    if (!completionData) return null;
    return {
      labels: completionData.completion_by_period.map((p) =>
        format(new Date(p.period_start), 'MMM dd')
      ),
      datasets: [
        {
          label: 'Planned',
          data: completionData.completion_by_period.map((p) => p.planned),
          backgroundColor: 'rgba(201, 203, 207, 0.6)',
        },
        {
          label: 'Completed',
          data: completionData.completion_by_period.map((p) => p.completed),
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
        },
        {
          label: 'Cancelled',
          data: completionData.completion_by_period.map((p) => p.cancelled),
          backgroundColor: 'rgba(255, 99, 132, 0.6)',
        },
      ],
    };
  }, [completionData]);

  // Priority Breakdown Chart Data
  const priorityBreakdownData = useMemo(() => {
    if (!completionData) return null;
    const priorities = Object.entries(completionData.by_priority);
    return {
      labels: priorities.map(([priority]) => priority.charAt(0).toUpperCase() + priority.slice(1)),
      datasets: [
        {
          label: 'Completion by Priority',
          data: priorities.map(([, stats]) => stats.completion_rate),
          backgroundColor: [
            'rgba(255, 99, 132, 0.6)',
            'rgba(255, 206, 86, 0.6)',
            'rgba(75, 192, 192, 0.6)',
          ],
        },
      ],
    };
  }, [completionData]);

  // Progression Chart Data
  const progressionChartData = useMemo(() => {
    if (!progressionData) return null;
    return {
      labels: progressionData.time_series.map((dp) => format(new Date(dp.date), 'MMM dd')),
      datasets: [
        {
          label: progressionMetric.charAt(0).toUpperCase() + progressionMetric.slice(1),
          data: progressionData.time_series.map((dp) => dp.metric_value),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.1)',
          tension: 0.4,
          fill: true,
        },
      ],
    };
  }, [progressionData, progressionMetric]);

  const handleCourseClick = (courseId: number) => {
    navigate(`/athlete/courses/${courseId}/analytics`);
  };

  const handleViewCourseDetails = (courseId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedWorkoutCourse(courseId);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Typography variant="h4" component="h1" fontWeight="bold">
                Workout Analytics
              </Typography>
              {selectedWorkoutCourse !== 'all' && simpleWorkoutsData && (
                <Chip
                  label={simpleWorkoutsData.find(w => w.id === selectedWorkoutCourse)?.name || 'Selected Workout'}
                  color="primary"
                  onDelete={() => setSelectedWorkoutCourse('all')}
                  size="medium"
                />
              )}
            </Box>
            <Typography variant="body1" color="text.secondary">
              {selectedWorkoutCourse === 'all'
                ? 'Comprehensive insights into your workout performance'
                : 'Focused analytics for selected workout'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 200 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Workout</InputLabel>
              <Select
                value={selectedWorkoutCourse}
                label="Workout"
                onChange={(e) => setSelectedWorkoutCourse(e.target.value as number | 'all')}
              >
                <MenuItem value="all">All Workouts</MenuItem>
                {simpleWorkoutsData?.map((workout) => (
                  <MenuItem key={workout.id} value={workout.id}>
                    {workout.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 200 }}>
            <PeriodSelector
              value={periodDays}
              onChange={setPeriodDays}
              label="Analysis Period"
              fullWidth
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="workout analytics tabs">
          <Tab icon={<FitnessCenterIcon />} label="Summary" />
          <Tab icon={<PedalBikeIcon />} label="Equipment" />
          <Tab icon={<CheckCircleIcon />} label="Completion" />
          <Tab icon={<TrendingUpIcon />} label="Progression" />
        </Tabs>
      </Box>

      {/* Tab 1: Summary */}
      <TabPanel value={tabValue} index={0}>
        {isLoadingSummary ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress size={60} />
          </Box>
        ) : summaryError ? (
          <Alert severity="error">Failed to load workout summary</Alert>
        ) : summaryData ? (
          <>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <KPICard
                  title="Total Workouts"
                  value={summaryData.total_workouts}
                  subtitle={`${periodDays} days`}
                  icon={<FitnessCenterIcon />}
                  color="primary"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <KPICard
                  title="Completion Rate"
                  value={`${summaryData.completion_rate.toFixed(1)}%`}
                  subtitle={`${summaryData.completed_workouts} completed`}
                  icon={<CheckCircleIcon />}
                  color="success"
                  trend={summaryData.completion_rate >= 80 ? 'up' : summaryData.completion_rate >= 60 ? 'stable' : 'down'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <KPICard
                  title="Total Distance"
                  value={`${(summaryData.total_distance / 1000).toFixed(1)} km`}
                  subtitle="All workouts"
                  icon={<DirectionsRunIcon />}
                  color="info"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <KPICard
                  title="Total Time"
                  value={`${Math.round(summaryData.total_time / 3600)} hrs`}
                  subtitle="Training time"
                  icon={<TimerIcon />}
                  color="secondary"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <KPICard
                  title="Avg TSS"
                  value={(summaryData.average_tss || 0).toFixed(0)}
                  subtitle="Per workout"
                  icon={<LocalFireDepartmentIcon />}
                  color="warning"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <KPICard
                  title="Total Elevation"
                  value={`${Math.round(summaryData.total_elevation)} m`}
                  subtitle="Climbed"
                  color="default"
                />
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              {sportDistributionData && (
                <Grid item xs={12} md={5}>
                  <Paper sx={{ p: 2, height: 400, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom>
                      Sport Distribution
                    </Typography>
                    <Box sx={{ flex: 1, position: 'relative' }}>
                      <Doughnut
                        data={sportDistributionData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { position: 'bottom' },
                          },
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              )}
              <Grid item xs={12} md={7}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Course Performance
                  </Typography>
                  <TableContainer sx={{ maxHeight: 350 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Course</strong></TableCell>
                          <TableCell align="right"><strong>Instances</strong></TableCell>
                          <TableCell align="right"><strong>Completed</strong></TableCell>
                          <TableCell align="right"><strong>Rate</strong></TableCell>
                          <TableCell align="right"><strong>Avg Pace</strong></TableCell>
                          <TableCell align="center"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {coursePerformanceData.map((course) => (
                          <TableRow
                            key={course.course_id}
                            hover
                            sx={{
                              backgroundColor: selectedWorkoutCourse === course.course_id ? 'action.selected' : 'transparent'
                            }}
                          >
                            <TableCell>{course.course_name}</TableCell>
                            <TableCell align="right">{course.total_instances}</TableCell>
                            <TableCell align="right">{course.completed_instances}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${course.completion_rate.toFixed(0)}%`}
                                size="small"
                                color={course.completion_rate >= 80 ? 'success' : course.completion_rate >= 60 ? 'warning' : 'error'}
                              />
                            </TableCell>
                            <TableCell align="right">{course.avg_pace || 'N/A'}</TableCell>
                            <TableCell align="center">
                              <Box display="flex" gap={1} justifyContent="center">
                                <Tooltip title="View detailed analytics">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCourseClick(course.course_id)}
                                    color="primary"
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Filter by this course">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleViewCourseDetails(course.course_id, e)}
                                    color={selectedWorkoutCourse === course.course_id ? 'success' : 'default'}
                                  >
                                    <FilterAltIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          </>
        ) : (
          <Typography color="text.secondary">No data available</Typography>
        )}
      </TabPanel>

      {/* Tab 2: Equipment */}
      <TabPanel value={tabValue} index={1}>
        {isLoadingEquipment ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress size={60} />
          </Box>
        ) : equipmentError ? (
          <Alert severity="error">Failed to load equipment analytics</Alert>
        ) : equipmentData ? (
          <>
            <Typography variant="h6" gutterBottom>
              Most Used Equipment
            </Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {equipmentCards.map((equipment) => (
                <Grid item xs={12} sm={6} md={4} key={equipment.gear_id}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <PedalBikeIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">{equipment.name}</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {equipment.usage_count} workouts
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(equipment.total_distance / 1000).toFixed(1)} km total
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <Typography color="text.secondary">No equipment data available</Typography>
        )}
      </TabPanel>

      {/* Tab 3: Completion */}
      <TabPanel value={tabValue} index={2}>
        {isLoadingCompletion ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress size={60} />
          </Box>
        ) : completionError ? (
          <Alert severity="error">Failed to load completion analytics</Alert>
        ) : completionData ? (
          <>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Overall Completion"
                  value={`${completionData.overall_completion_rate.toFixed(1)}%`}
                  color="success"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Adherence Rate"
                  value={`${completionData.overall_adherence_rate.toFixed(1)}%`}
                  color="info"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Current Streak"
                  value={`${completionData.streak_current} days`}
                  icon={<LocalFireDepartmentIcon />}
                  color="warning"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Longest Streak"
                  value={`${completionData.streak_longest} days`}
                  color="primary"
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              {completionByPeriodData && (
                <Grid item xs={12} md={8}>
                  <Paper sx={{ p: 2, height: 400 }}>
                    <Typography variant="h6" gutterBottom>
                      Completion Over Time
                    </Typography>
                    <Box sx={{ height: 350 }}>
                      <Bar
                        data={completionByPeriodData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { position: 'top' },
                          },
                          scales: {
                            x: { stacked: false },
                            y: { stacked: false, beginAtZero: true },
                          },
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              )}
              {priorityBreakdownData && (
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, height: 400 }}>
                    <Typography variant="h6" gutterBottom>
                      By Priority
                    </Typography>
                    <Box sx={{ height: 350 }}>
                      <Doughnut
                        data={priorityBreakdownData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { position: 'bottom' },
                          },
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </>
        ) : (
          <Typography color="text.secondary">No completion data available</Typography>
        )}
      </TabPanel>

      {/* Tab 4: Progression */}
      <TabPanel value={tabValue} index={3}>
        <Box sx={{ mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Metric</InputLabel>
            <Select
              value={progressionMetric}
              label="Metric"
              onChange={(e) => setProgressionMetric(e.target.value)}
            >
              <MenuItem value="pace">Pace</MenuItem>
              <MenuItem value="power">Power</MenuItem>
              <MenuItem value="hr">Heart Rate</MenuItem>
              <MenuItem value="speed">Speed</MenuItem>
              <MenuItem value="tss">TSS</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {isLoadingProgression ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress size={60} />
          </Box>
        ) : progressionError ? (
          <Alert severity="error">Failed to load progression analytics</Alert>
        ) : progressionData ? (
          <>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Trend"
                  value={progressionData.trend.charAt(0).toUpperCase() + progressionData.trend.slice(1)}
                  color={
                    progressionData.trend === 'improving'
                      ? 'success'
                      : progressionData.trend === 'declining'
                      ? 'error'
                      : 'warning'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Improvement"
                  value={`${progressionData.improvement_percentage > 0 ? '+' : ''}${progressionData.improvement_percentage.toFixed(1)}%`}
                  trend={progressionData.improvement_percentage > 0 ? 'up' : progressionData.improvement_percentage < 0 ? 'down' : 'stable'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Weekly Improvement"
                  value={`${progressionData.average_improvement_per_week > 0 ? '+' : ''}${progressionData.average_improvement_per_week.toFixed(2)}%`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Data Points"
                  value={progressionData.time_series.length}
                  subtitle="Workouts analyzed"
                />
              </Grid>
            </Grid>

            {progressionChartData && (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Performance Progression
                </Typography>
                <Box sx={{ height: 400 }}>
                  <Line
                    data={progressionChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { position: 'top' },
                      },
                      scales: {
                        y: { beginAtZero: false },
                      },
                    }}
                  />
                </Box>
              </Paper>
            )}
          </>
        ) : (
          <Typography color="text.secondary">No progression data available</Typography>
        )}
      </TabPanel>
    </Container>
  );
};

export default WorkoutAnalyticsPage;
